<template>
  <Dialog v-model:visible="visible" modal class="p-dialog p-dialog-wide">
    <template #container="{}">
      <div class="p-dialog-content">
        <div class="modal-body welcome-modal">
          <img src="/welcome.png" class="welcome-modal__image" />

          <div class="welcome-modal__header">Welcome to Meshh Vantage</div>
          <div class="welcome-modal__message">
            Discover powerful insights for your events and spaces.<br />Let's get you started!
          </div>

          <div class="welcome-modal__controls">
            <Button severity="primary" @click="onStart()">
              <IconRocket />
              Take a Guided Tour
            </Button>
            <Button outlined @click="onSkip()">
              <IconStar />
              Start Using Meshh Vantage
            </Button>
          </div>

          <div class="welcome-modal__check">
            <Checkbox
              v-model="shutdownWelcome"
              inputId="shutdownWelcome"
              name="shutdownWelcome"
              value="disable welcome message?"
            />
            <label for="shutdownWelcome">Don't show this again</label>
          </div>
        </div>
      </div>
    </template>
  </Dialog>

  <VOnboardingWrapper ref="wrapper" :steps="steps" :options="options" @finish="onFinish">
    <template #default="{ previous, next, step, isFirst, isLast, index }">
      <VOnboardingStep>
        <div class="onboarding-container">
          <div v-if="step.content" class="onboarding-content">
            <div class="onboarding-heading">{{ `Step ${index + 1} of ${steps.length}` }}</div>
            <h3 v-if="step.content.title" class="onboarding-title">{{ step.content.title }}</h3>
            <p v-if="step.content.description">{{ step.content.description }}</p>
          </div>

          <div class="onboarding-buttons">
            <Button severity="secondary" outlined @click="onSkip()">
              <WalkthroughSkip />
              Skip All
            </Button>

            <Button v-if="!isFirst" severity="primary" outlined @click="previous">
              Previous
            </Button>
            <Button severity="primary" outlined @click="next">
              {{ isLast ? 'Finish' : 'Next' }}
            </Button>
          </div>
        </div>
      </VOnboardingStep>
    </template>
  </VOnboardingWrapper>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { VOnboardingWrapper, VOnboardingStep, useVOnboarding } from 'v-onboarding';
import type { StepEntity } from 'v-onboarding';
import 'v-onboarding/dist/style.css';

import { updateUserSelf } from '@/utils/api';

import { Button, Checkbox, Dialog } from 'primevue';

import WalkthroughSkip from '@/icons/WalkthroughSkip.svg?component';
import IconRocket from '@/icons/welcome/IconRocket.svg?component';
import IconStar from '@/icons/welcome/IconStar.svg?component';

import { useCookies } from '@/utils/cookies';

import { useAnalyticsStore } from '@/stores/analytics';
import { useEventsStore } from '@/stores/events';
import { useSensorsStore } from '@/stores/sensors';
import { useUsersStore } from '@/stores/users';

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

const cookies = useCookies();

const router = useRouter();
const route = useRoute();

const shutdownWelcome = ref(false);
const visible = ref<boolean>(false);
const tourActive = ref<boolean>(false);

const wrapper = ref(null);
const vBoarding = useVOnboarding(wrapper);

// v-onboarding doesn't precisely export what I need.
type OnStep = ReturnType<StepEntity['on'] | any>;
type BeforeStep = ReturnType<OnStep['beforeStep']>;

watch(route, (newVal) => {
  if (newVal.path === '/' && userStore.userData) {
    if (!userStore.userData.vantage_tour_completed && !tourActive.value) {
      startTour();
    }
  }
  if (newVal.hash === '#tour') {
    startTour();
  }
});

const startTour = () => {
  const analyticsStore = useAnalyticsStore();
  analyticsStore.getDemoEventData().then(() => {
    analyticsStore.useDemoEvent();
    visible.value = true;
  });
};

const options = {
  scrollToStep: {
    enabled: false,
  },
  hideNextStepDuringHook: true,
};

const steps = ref<StepEntity[]>([
  {
    attachTo: { element: '[data-step="0"]' },
    content: {
      title: 'View your event metrics',
      description: 'The event metrics dashboard is the heart of Vantage.',
    },
    on: {
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          if (route.path != '/') {
            router.push('/');
            setTimeout(() => {
              window.scroll({
                top: 0,
                left: 0,
                behavior: 'smooth',
              });
              resolve();
            }, 1000);
          } else {
            window.scroll({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
            resolve();
          }
        });
      },
    },
  },
  {
    attachTo: { element: '[data-step="1"]' },
    content: {
      title: 'Deep dive into an event',
      description: 'Pick an event from the drop-down list and view its visit metrics.',
    },
  },
  {
    attachTo: { element: '[data-step="2"]' },
    content: {
      title: 'View event configuration',
      description: 'Look into the deployment plan, zones and sensors for this event.',
    },
  },
  {
    attachTo: { element: '[data-step="3"]' },
    content: {
      title: 'View metrics',
      description:
        'Each metric widget has additional information (tooltip) and various filters to slice and dice the metric.',
    },
    on: {
      beforeStep: (options: BeforeStep) => {
        if (options.isBackward) {
          return new Promise<void>((resolve) => {
            const analyticsStore = useAnalyticsStore();
            router.push('/');
            watch(analyticsStore, async () => {
              if (!analyticsStore.loading) {
                await nextTick();
                resolve();
              }
            });
          });
        }
      },
    },
  },
  {
    attachTo: { element: '[data-step="4"]' },
    content: {
      title: 'View your events',
      description: 'See your events across all time. Search and filter them by date.',
    },
    on: {
      beforeStep: (options: BeforeStep) => {
        if (options.isForward) {
          return new Promise<void>((resolve) => {
            const eventsStore = useEventsStore();
            router.push('/events');
            watch(eventsStore, async () => {
              if (!eventsStore.loading) {
                await nextTick();
                resolve();
              }
            });
          });
        }
      },
    },
  },
  {
    attachTo: { element: '[data-step="5"]' },
    content: {
      title: 'Create a new Event',
      description: 'Takes you through a 5-step process to create a new event',
    },
    on: {
      beforeStep: (options: BeforeStep) => {
        if (options.isBackward) {
          return new Promise<void>((resolve) => {
            const eventsStore = useEventsStore();
            router.push('/events');
            watch(eventsStore, async () => {
              if (!eventsStore.loading) {
                await nextTick();
                resolve();
              }
            });
          });
        }
        return;
      },
    },
  },
  {
    attachTo: { element: '[data-step="6"]' },
    content: {
      title: 'Setup / View users',
      description: 'View and add your colleagues to the Meshh Vantage platform.',
    },
    on: {
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          const usersStore = useUsersStore();
          router.push('/users');
          watch(usersStore, async () => {
            if (!usersStore.loading) {
              await nextTick();
              resolve();
            }
          });
        });
      },
    },
  },
  {
    attachTo: { element: '[data-step="7"]' },
    content: {
      title: 'Sensors',
      description: 'View the sensors assigned to your account.',
    },
    on: {
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          const sensorsStore = useSensorsStore();
          router.push('/sensors');
          watch(sensorsStore, async () => {
            if (!sensorsStore.loading) {
              await nextTick();
              resolve();
            }
          });
        });
      },
    },
  },
  {
    attachTo: { element: '[data-step="8"]' },
    content: {
      title: 'Questions?',
      description:
        'Head to our FAQ where you can search and find answers to anything from sensor deployment to data privacy.',
    },
    on: {
      beforeStep: (options: BeforeStep) => {
        if (options.isForward) {
          return new Promise<void>((resolve) => {
            router.push('/faq');
            resolve();
          });
        }
      },
    },
  },
  {
    attachTo: { element: '[data-step="9"]' },
    content: {
      title: 'Your profile',
      description: 'View your user profile and update your details.',
    },
    on: {
      beforeStep: () => {
        return new Promise<void>((resolve) => {
          router.push('/user-profile');
          resolve();
        });
      },
    },
  },
]);

const onSkip = () => {
  visible.value = false;
  vBoarding.finish();

  if (shutdownWelcome.value) {
    const payload = {
      vantage_tour_completed: true,
    };

    updateUserSelf(userStore.accessToken, payload).then(() => {
      if (userStore.userData) {
        userStore.userData.vantage_tour_completed = true;
        cookies.set('user', userStore.userData);
      }
    });
  }
};

const onFinish = () => {
  // Only display the walkthrough invite once per login.
  if (userStore.userData) {
    userStore.userData.vantage_tour_completed = true;
    cookies.set('user', userStore.userData);
  }

  // Navigate to home page when tour is finished
  router.push('/');
};

const onStart = () => {
  visible.value = false;
  tourActive.value = true;
  vBoarding.start();
};
</script>

<style lang="scss">
.onboarding-heading {
  @include typography.font-body($size: sm, $weight: medium);
  color: variables.$vantage-base-primitives-color-background-100;
  opacity: 0.45;
}

.onboarding-title {
  @include typography.font-display($size: xs, $weight: bold);
  @include sizing.margin('xxs', 0);
}

.onboarding-content {
  @include sizing.padding('xs', 'xs', 0, 'xs');
  @include typography.font-body($size: sm, $weight: normal);
  color: variables.$vantage-base-primitives-color-background-100;

  p {
    color: variables.$vantage-base-primitives-color-background-100;
  }
}

.onboarding-container {
  @include mixins.flex-center($horiz: flex-start, $wrap: true);
  @include sizing.padding('xs');
  background-color: variables.$vantage-base-primitives-color-blue-300;
  border-radius: variables.$vantage-base-sizing-xxxs;
  box-shadow: 0px 6px 24px 0px #0000001f;
  height: 200px;
  width: 320px;
}

.onboarding-buttons {
  @include mixins.flex-center($horiz: flex-end, $gap: variables.$vantage-base-sizing-xxs);
  flex-basis: 100%;
  flex-shrink: 0;
  margin-top: auto;

  .p-button-outlined.p-button-primary,
  .p-button-outlined.p-button-secondary {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-xxxs);
    @include sizing.padding('xxs', 'xs');
    background-color: transparent;
    border-color: transparent;
    border-radius: variables.$vantage-base-sizing-xxxs;
    color: variables.$vantage-base-primitives-color-background-100;
    cursor: pointer;
    width: auto;

    &:not(:disabled):hover {
      background-color: variables.$vantage-base-primitives-color-background-100;
      color: variables.$vantage-base-primitives-color-blue-300;
    }
  }

  .p-button-outlined.p-button-primary {
    @include typography.font-body($size: sm, $weight: bold);
  }

  .p-button-outlined.p-button-secondary {
    @include typography.font-body($size: xs, $weight: normal);
    margin-right: auto;

    svg {
      height: variables.$vantage-base-sizing-md;
      width: variables.$vantage-base-sizing-md;
    }
  }
}

.welcome-modal {
  text-align: center;

  .welcome-modal__image {
    margin-bottom: variables.$vantage-base-sizing-lg;
    width: 260px;
  }

  .welcome-modal__header {
    @include typography.font-display($size: sm, $weight: bold);
    color: variables.$vantage-base-primitives-color-tertiary-600;
  }

  .welcome-modal__message {
    @include sizing.margin('xs', 'auto');
    @include typography.font-body($size: md, $weight: normal);
    line-height: 20px;
    width: 220px;
  }

  .welcome-modal__controls {
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-lg);
    margin-top: variables.$vantage-base-sizing-lg;
  }

  .welcome-modal__check {
    @include typography.font-body($size: sm, $weight: normal);
    @include mixins.flex-center($gap: variables.$vantage-base-sizing-xs);
    cursor: pointer;
    margin-top: variables.$vantage-base-sizing-xs;
  }
}
</style>
